class PredictiveSearch extends SearchForm {
  connectedCallback() {
    super.connectedCallback();
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.statusElement = this.querySelector('[role="status"]');
    this.loadingMarkup = this.predictiveSearchResults.innerHTML;
    this.resultsId = `${this.input.id}-results`;
    this.requestId = 0;
    this.cachedResults = new Map();
    this.isOpen = false;
    const options = { signal: this.events.signal };
    this.input.addEventListener('focus', () => { if (this.getQuery()) this.onChange(); }, options);
    this.input.addEventListener('keydown', event => this.onKeydown(event), options);
    this.input.form.addEventListener('submit', event => {
      if (!this.getQuery()) event.preventDefault();
    }, options);
    this.addEventListener('focusout', event => {
      if (!this.contains(event.relatedTarget)) this.close();
    }, options);
  }

  disconnectedCallback() {
    this.close();
    super.disconnectedCallback();
  }

  getQuery() { return this.input.value.trim(); }

  onInputChange() {
    // Invalidate immediately, before the debounce, including native search clear.
    this.close();
  }

  onChange() {
    super.onChange();
    const query = this.getQuery();
    if (!query || document.activeElement !== this.input) return;
    this.getSearchResults(query);
  }

  onKeydown(event) {
    if (event.isComposing || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key === 'Escape' && (this.isOpen || this.hasAttribute('loading'))) {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!this.isOpen) return;
      event.preventDefault();
      const options = [...this.predictiveSearchResults.querySelectorAll('[role="option"]')];
      if (!options.length) return;
      const index = options.findIndex(option => option.getAttribute('aria-selected') === 'true');
      const next = index < 0 ? (event.key === 'ArrowDown' ? 0 : options.length - 1)
        : (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
      options.forEach(option => option.setAttribute('aria-selected', 'false'));
      options[next].setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', options[next].id);
      options[next].scrollIntoView({ block: 'nearest', inline: 'nearest' });
    } else if (event.key === 'Enter' && this.isOpen) {
      const selected = this.predictiveSearchResults.querySelector('[aria-selected="true"]');
      if (selected) { event.preventDefault(); selected.click(); }
    } else if (event.key === 'Tab') {
      this.close();
    }
  }

  async getSearchResults(query) {
    this.abortController?.abort();
    const requestId = ++this.requestId;
    this.abortController = new AbortController();
    this.setAttribute('loading', '');
    this.statusElement.textContent = this.dataset.loadingText;
    try {
      let markup = this.cachedResults.get(query);
      if (!markup) {
        const response = await fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(query)}&section_id=predictive-search`, { signal: this.abortController.signal });
        if (!response.ok) throw new Error(`Predictive search: ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = html.querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Predictive search: missing section');
        markup = section.innerHTML;
      }
      if (requestId !== this.requestId || query !== this.getQuery() || !this.isConnected || document.activeElement !== this.input) return;
      // Bound the cache for a long-lived header/Theme Editor session.
      if (this.cachedResults.size >= 20) this.cachedResults.delete(this.cachedResults.keys().next().value);
      this.cachedResults.set(query, markup);
      this.renderSearchResults(markup);
    } catch (error) {
      if (error.name === 'AbortError' || requestId !== this.requestId) return;
      this.close();
      this.statusElement.textContent = this.dataset.errorText;
      console.warn('Predictive search unavailable; the search form remains usable.');
    }
  }

  renderSearchResults(markup) {
    this.predictiveSearchResults.innerHTML = markup;
    // Shopify's section endpoint is shared by header and search-page instances.
    const ids = new Map();
    this.predictiveSearchResults.querySelectorAll('[id]').forEach(element => {
      const oldId = element.id;
      const id = oldId === 'predictive-search-results' ? this.resultsId : `${this.resultsId}-${oldId}`;
      ids.set(oldId, id);
      element.id = id;
    });
    this.predictiveSearchResults.querySelectorAll('[aria-labelledby]').forEach(element => {
      element.setAttribute('aria-labelledby', element.getAttribute('aria-labelledby').split(/\s+/).map(id => ids.get(id) || id).join(' '));
    });
    this.input.setAttribute('aria-controls', this.resultsId);
    this.input.setAttribute('aria-expanded', 'true');
    this.input.removeAttribute('aria-activedescendant');
    this.removeAttribute('loading');
    this.setAttribute('open', '');
    this.isOpen = true;
    const top = this.predictiveSearchResults.getBoundingClientRect().top;
    this.predictiveSearchResults.style.maxHeight = `${Math.max(120, window.innerHeight - top - 16)}px`;
    this.statusElement.textContent = this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent.trim() || '';
  }

  close() {
    clearTimeout(this.changeTimer);
    ++this.requestId;
    this.abortController?.abort();
    this.isOpen = false;
    this.removeAttribute('open');
    this.removeAttribute('loading');
    this.input?.setAttribute('aria-expanded', 'false');
    this.input?.removeAttribute('aria-activedescendant');
    this.input?.removeAttribute('aria-controls');
    if (this.statusElement) this.statusElement.textContent = '';
    if (this.predictiveSearchResults) this.predictiveSearchResults.innerHTML = this.loadingMarkup;
  }
}
customElements.define('predictive-search', PredictiveSearch);
